import { useEffect, useRef, useState } from "react";
import Icon from "../Icon";
import { InputField, Props as InputFieldProps } from "../InputField";
import { PasswordField } from "../PasswordField";
import s from "./CopyField.module.css";
import cn from "classnames";
import { delayAfterSuccessfulCopy } from "../../constants";

export type Props = Pick<
  InputFieldProps,
  | "value"
  | "defaultValue"
  | "type"
  | "readOnly"
  | "useValueState"
  | "color"
  | "onChange"
> & {
  isRotating?: boolean;
  onReload?: () => void;
};

export function CopyField({
  color = "purple",
  defaultValue = "",
  isRotating = false,
  onReload,
  ...props
}: Props) {
  const [isCopied, setIsCopied] = useState(false);
  const fieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (fieldRef.current) {
      fieldRef.current.value = String(defaultValue);
    }
  }, [defaultValue]);

  const fieldProps = {
    ...props,
    readOnly: true,
    isError: false,
    color,
    labelClassName: s["copy-field__input"],
    ref: fieldRef,
    defaultValue,
  };

  const field =
    props.type === "password" ? (
      <PasswordField {...fieldProps} icon="toggle" />
    ) : (
      <InputField {...fieldProps} />
    );

  function copyFieldValue() {
    if (!fieldRef.current) return;
    navigator.clipboard
      .writeText(fieldRef.current.value)
      .then(() => setIsCopied(true))
      .catch((error) => console.error(error));
    setTimeout(() => {
      setIsCopied(false);
    }, delayAfterSuccessfulCopy);
  }

  return props.type === "password" ? (
    <div
      className={cn(s["copy-field__reload-button"], s[`copy-field_${color}`])}
    >
      {field}
      <div className={cn(s["copy-field-buttons"])}>
        <button
          type="button"
          className={cn(s["reload-button"], {
            [s["reload-button_is_rotating"]]: isRotating,
          })}
          onClick={onReload}
        >
          <Icon name="reload" />
        </button>
        <button
          type="button"
          className={s["copy-field__button"]}
          onClick={copyFieldValue}
        >
          <Icon name={isCopied ? "done" : "copy"} />
        </button>
      </div>
    </div>
  ) : (
    <div className={cn(s["copy-field"], s[`copy-field_${color}`])}>
      {field}
      <div className={cn(s["copy-field-buttons"])}>
        <button
          type="button"
          className={s["copy-field__button"]}
          onClick={copyFieldValue}
        >
          <Icon name={isCopied ? "done" : "copy"} />
        </button>
      </div>
    </div>
  );
}
